import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { StatsCards } from '../../components/StatsCards';
import { FilterBar } from '../../components/FilterBar';
import { bibliothequeService, Marabout, Livre, Sourate } from '../../services/bibliothequeService';
import api from '../../config/api';

export const AdminBibliotheque: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'marabouts' | 'livres' | 'coran'>('marabouts');
  const [allMarabouts, setAllMarabouts] = useState<Marabout[]>([]);
  const [allLivres, setAllLivres] = useState<Livre[]>([]);
  const [allSourates, setAllSourates] = useState<Sourate[]>([]);
  const [marabouts, setMarabouts] = useState<Marabout[]>([]);
  const [livres, setLivres] = useState<Livre[]>([]);
  const [sourates, setSourates] = useState<Sourate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [stats, setStats] = useState({
    marabouts: 0,
    livres: 0,
    sourates: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    categorie: '',
    langue: '',
  });
  
  // États de pagination séparés pour chaque onglet
  const [currentPageMarabouts, setCurrentPageMarabouts] = useState(1);
  const [totalPagesMarabouts, setTotalPagesMarabouts] = useState(1);
  const [currentPageLivres, setCurrentPageLivres] = useState(1);
  const [totalPagesLivres, setTotalPagesLivres] = useState(1);
  const [currentPageCoran, setCurrentPageCoran] = useState(1);
  const [totalPagesCoran, setTotalPagesCoran] = useState(1);

  useEffect(() => {
    fetchStats();
    fetchAllData();
  }, []);

  useEffect(() => {
    if (activeTab === 'marabouts') {
      applyFiltersMarabouts();
    } else if (activeTab === 'livres') {
      applyFiltersLivres();
    } else if (activeTab === 'coran') {
      applyFiltersSourates();
    }
  }, [filters, allMarabouts, allLivres, allSourates, activeTab, currentPageMarabouts, currentPageLivres, currentPageCoran]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [maraboutsData, livresData, souratesData] = await Promise.all([
        bibliothequeService.getMarabouts(1, 1000).catch(() => ({ results: [] })),
        bibliothequeService.getLivres(1, 1000).catch(() => ({ results: [] })),
        bibliothequeService.getSourates(1, 1000).catch(() => ({ results: [] })),
      ]);
      
      setAllMarabouts(Array.isArray(maraboutsData) ? maraboutsData : (maraboutsData.results || []));
      setAllLivres(Array.isArray(livresData) ? livresData : (livresData.results || []));
      setAllSourates(Array.isArray(souratesData) ? souratesData : (souratesData.results || []));
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [maraboutsData, livresData, souratesData] = await Promise.all([
        bibliothequeService.getMarabouts(1, 1000).catch(() => ({ results: [] })),
        bibliothequeService.getLivres(1, 1000).catch(() => ({ results: [] })),
        bibliothequeService.getSourates(1, 1000).catch(() => ({ results: [] })),
      ]);
      
      const statsData = {
        marabouts: Array.isArray(maraboutsData) ? maraboutsData.length : ((maraboutsData as any).count || (maraboutsData.results || []).length),
        livres: Array.isArray(livresData) ? livresData.length : ((livresData as any).count || (livresData.results || []).length),
        sourates: Array.isArray(souratesData) ? souratesData.length : ((souratesData as any).count || (souratesData.results || []).length),
      };
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const applyFiltersMarabouts = () => {
    let filtered = [...allMarabouts];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((item) =>
        item.nom_complet?.toLowerCase().includes(searchLower) ||
        item.nom_arabe?.toLowerCase().includes(searchLower) ||
        item.periode?.toLowerCase().includes(searchLower)
      );
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || 0).getTime();
      const dateB = new Date((b as any).created_at || 0).getTime();
      return dateB - dateA;
    });

    const pageSize = 6;
    const startIndex = (currentPageMarabouts - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filtered.slice(startIndex, endIndex);

    setMarabouts(paginated);
    setTotalPagesMarabouts(Math.ceil(filtered.length / pageSize));
  };

  const applyFiltersLivres = () => {
    let filtered = [...allLivres];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((item) =>
        item.titre?.toLowerCase().includes(searchLower) ||
        item.auteur_nom?.toLowerCase().includes(searchLower) ||
        item.auteur_autre?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.categorie) {
      filtered = filtered.filter((item) => item.categorie === filters.categorie);
    }

    if (filters.langue) {
      filtered = filtered.filter((item) => item.langue === filters.langue);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || 0).getTime();
      const dateB = new Date((b as any).created_at || 0).getTime();
      return dateB - dateA;
    });

    const pageSize = 6;
    const startIndex = (currentPageLivres - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filtered.slice(startIndex, endIndex);

    setLivres(paginated);
    setTotalPagesLivres(Math.ceil(filtered.length / pageSize));
  };

  const applyFiltersSourates = () => {
    let filtered = [...allSourates];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((item) =>
        item.nom_francais?.toLowerCase().includes(searchLower) ||
        item.nom_arabe?.toLowerCase().includes(searchLower) ||
        item.numero?.toString().includes(searchLower)
      );
    }

    // Trier par numéro de sourate (ordre croissant pour les sourates)
    filtered.sort((a, b) => {
      return (a.numero || 0) - (b.numero || 0);
    });

    const pageSize = 8;
    const startIndex = (currentPageCoran - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filtered.slice(startIndex, endIndex);

    setSourates(paginated);
    setTotalPagesCoran(Math.ceil(filtered.length / pageSize));
  };

  const handleCreate = () => {
    setFormData({});
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: any) => {
    if (activeTab === 'marabouts') {
      setFormData({
        nom_complet: item.nom_complet,
        nom_arabe: item.nom_arabe || '',
        biographie: item.biographie,
        date_naissance: item.date_naissance || '',
        date_deces: item.date_deces || '',
        lieu_naissance: item.lieu_naissance || '',
        periode: item.periode || '',
      });
    } else {
      setFormData({
        titre: item.titre,
        auteur: item.auteur?.id || '',
        auteur_autre: item.auteur_autre || '',
        categorie: item.categorie,
        langue: item.langue,
        description: item.description || '',
        nombre_pages: item.nombre_pages || '',
        date_publication: item.date_publication || '',
        isbn: item.isbn || '',
      });
    }
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };

  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedItem) {
        await api.put(`/bibliotheque/${activeTab}/${selectedItem.id}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post(`/bibliotheque/${activeTab}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setIsModalOpen(false);
      await fetchAllData();
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;
    try {
      await api.delete(`/bibliotheque/${activeTab}/${selectedItem.id}/`);
      setIsDeleteModalOpen(false);
      await fetchAllData();
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const maraboutColumns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'nom_complet', 
      label: 'Nom complet',
      render: (value: string) => <strong className="text-green-700">{value}</strong>
    },
    { key: 'nom_arabe', label: 'Nom arabe' },
    { key: 'periode', label: 'Période' },
    {
      key: 'nombre_livres',
      label: 'Livres',
      render: (value: number) => <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">{value || 0}</span>
    },
  ];

  const livreColumns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'titre', 
      label: 'Titre',
      render: (value: string) => <strong className="text-green-700">{value}</strong>
    },
    {
      key: 'auteur',
      label: 'Auteur',
      render: (_: any, row: Livre) => row.auteur_nom || row.auteur_autre || 'Inconnu'
    },
    {
      key: 'categorie',
      label: 'Catégorie',
      render: (value: string) => (
        <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-semibold">
          {value}
        </span>
      )
    },
    {
      key: 'langue',
      label: 'Langue',
      render: (value: string) => (
        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
          {value}
        </span>
      )
    },
  ];

  if (loading) {
    return (
      <AdminLayout activeSection="bibliotheque">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="bibliotheque">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-green-700">Gestion de la Bibliothèque</h2>
          <p className="text-gray-600 mt-1">Marabouts, Livres et Coran</p>
        </div>
        {activeTab !== 'coran' && (
          <button onClick={handleCreate} className="btn-primary text-lg px-6 py-3">
            + Nouveau {activeTab === 'marabouts' ? 'Marabout' : 'Livre'}
          </button>
        )}
      </div>

      {/* Cartes de statistiques */}
      <StatsCards
        stats={[
          { label: 'Total Marabouts', value: stats.marabouts, icon: '📚', color: 'amber' },
          { label: 'Total Livres', value: stats.livres, icon: '📖', color: 'indigo' },
          { label: 'Total Sourates', value: stats.sourates, icon: '📿', color: 'green' },
          { label: 'Total', value: stats.marabouts + stats.livres + stats.sourates, icon: '📚', color: 'purple' },
        ]}
      />

      {/* Filtres */}
      <FilterBar
        searchPlaceholder={
          activeTab === 'marabouts' 
            ? '🔍 Rechercher par nom, nom arabe, période...'
            : activeTab === 'livres'
            ? '🔍 Rechercher par titre, auteur...'
            : '🔍 Rechercher par nom français, nom arabe, numéro...'
        }
        searchValue={filters.search}
        onSearchChange={(value) => {
          setFilters({ ...filters, search: value });
          if (activeTab === 'marabouts') setCurrentPageMarabouts(1);
          else if (activeTab === 'livres') setCurrentPageLivres(1);
          else setCurrentPageCoran(1);
        }}
        filters={
          activeTab === 'livres'
            ? [
                {
                  key: 'categorie',
                  label: 'Toutes les catégories',
                  value: filters.categorie,
                  onChange: (value) => {
                    setFilters({ ...filters, categorie: value });
                    setCurrentPageLivres(1);
                  },
                  options: [
                    { value: 'coran', label: '📿 Coran' },
                    { value: 'hadith', label: '📖 Hadith' },
                    { value: 'fiqh', label: '⚖️ Fiqh' },
                    { value: 'tasawwuf', label: '🧘 Tasawwuf' },
                    { value: 'histoire', label: '📜 Histoire' },
                    { value: 'biographie', label: '👤 Biographie' },
                    { value: 'autre', label: '📌 Autre' },
                  ],
                },
                {
                  key: 'langue',
                  label: 'Toutes les langues',
                  value: filters.langue,
                  onChange: (value) => {
                    setFilters({ ...filters, langue: value });
                    setCurrentPageLivres(1);
                  },
                  options: [
                    { value: 'arabe', label: '🇸🇦 Arabe' },
                    { value: 'francais', label: '🇫🇷 Français' },
                    { value: 'wolof', label: '🇸🇳 Wolof' },
                    { value: 'autre', label: '🌐 Autre' },
                  ],
                },
              ]
            : []
        }
        onClear={() => {
          setFilters({ search: '', categorie: '', langue: '' });
          if (activeTab === 'marabouts') setCurrentPageMarabouts(1);
          else if (activeTab === 'livres') setCurrentPageLivres(1);
          else setCurrentPageCoran(1);
        }}
      />

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md mb-6 p-2 flex space-x-2">
        <button
          onClick={() => setActiveTab('marabouts')}
          className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
            activeTab === 'marabouts'
              ? 'bg-green-700 text-white shadow-lg transform scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📚 Marabouts
        </button>
        <button
          onClick={() => setActiveTab('livres')}
          className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
            activeTab === 'livres'
              ? 'bg-green-700 text-white shadow-lg transform scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📖 Livres
        </button>
        <button
          onClick={() => setActiveTab('coran')}
          className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
            activeTab === 'coran'
              ? 'bg-green-700 text-white shadow-lg transform scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📿 Coran
        </button>
      </div>

      {activeTab === 'marabouts' ? (
        <>
          <Table
            columns={maraboutColumns}
            data={marabouts}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          <PaginationControls
            currentPage={currentPageMarabouts}
            totalPages={totalPagesMarabouts}
            loading={loading}
            onPageChange={(page) => {
              setCurrentPageMarabouts(page);
            }}
          />
        </>
      ) : activeTab === 'livres' ? (
        <>
          <Table
            columns={livreColumns}
            data={livres}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          <PaginationControls
            currentPage={currentPageLivres}
            totalPages={totalPagesLivres}
            loading={loading}
            onPageChange={(page) => {
              setCurrentPageLivres(page);
            }}
          />
        </>
      ) : (
        <>
          <Table
            columns={[
              { key: 'id', label: 'ID' },
              { key: 'numero', label: 'Numéro' },
              { 
                key: 'nom_francais', 
                label: 'Nom Français',
                render: (value: string) => <strong className="text-green-700">{value}</strong>
              },
              { key: 'nom_arabe', label: 'Nom Arabe', render: (value: string) => <span dir="rtl">{value}</span> },
              { key: 'nombre_versets', label: 'Versets' },
              { key: 'type_sourate', label: 'Type' },
            ]}
            data={sourates}
            onView={handleView}
            onEdit={undefined}
            onDelete={undefined}
          />
          <PaginationControls
            currentPage={currentPageCoran}
            totalPages={totalPagesCoran}
            loading={loading}
            onPageChange={(page) => {
              setCurrentPageCoran(page);
            }}
          />
        </>
      )}

      {/* Modal Création/Modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedItem ? `Modifier le ${activeTab === 'marabouts' ? 'marabout' : 'livre'}` : `Nouveau ${activeTab === 'marabouts' ? 'marabout' : 'livre'}`}
        size="lg"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={handleSubmit} className="btn-primary">
              Enregistrer
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {activeTab === 'marabouts' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.nom_complet || ''}
                  onChange={(e) => setFormData({ ...formData, nom_complet: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom arabe</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.nom_arabe || ''}
                  onChange={(e) => setFormData({ ...formData, nom_arabe: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Biographie *</label>
                <textarea
                  className="input-field min-h-[150px]"
                  value={formData.biographie || ''}
                  onChange={(e) => setFormData({ ...formData, biographie: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de naissance</label>
                  <input
                    type="date"
                    className="input-field"
                    value={formData.date_naissance || ''}
                    onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de décès</label>
                  <input
                    type="date"
                    className="input-field"
                    value={formData.date_deces || ''}
                    onChange={(e) => setFormData({ ...formData, date_deces: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.periode || ''}
                  onChange={(e) => setFormData({ ...formData, periode: e.target.value })}
                  placeholder="ex: 1853-1927"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.titre || ''}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Auteur (autre)</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.auteur_autre || ''}
                    onChange={(e) => setFormData({ ...formData, auteur_autre: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie *</label>
                  <select
                    className="input-field"
                    value={formData.categorie || ''}
                    onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner</option>
                    <option value="coran">Coran</option>
                    <option value="hadith">Hadith</option>
                    <option value="fiqh">Fiqh</option>
                    <option value="tasawwuf">Tasawwuf</option>
                    <option value="histoire">Histoire</option>
                    <option value="biographie">Biographie</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Langue *</label>
                  <select
                    className="input-field"
                    value={formData.langue || ''}
                    onChange={(e) => setFormData({ ...formData, langue: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner</option>
                    <option value="arabe">Arabe</option>
                    <option value="francais">Français</option>
                    <option value="wolof">Wolof</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de pages</label>
                  <input
                    type="number"
                    className="input-field"
                    value={formData.nombre_pages || ''}
                    onChange={(e) => setFormData({ ...formData, nombre_pages: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="input-field min-h-[100px]"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </>
          )}
        </form>
      </Modal>

      {/* Modal Détails */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={`Détails ${activeTab === 'marabouts' ? 'du marabout' : 'du livre'}`}
      >
        {selectedItem && (
          <div className="space-y-4">
            {activeTab === 'marabouts' ? (
              <>
                <div>
                  <p className="text-sm text-gray-600">Nom complet</p>
                  <p className="font-semibold text-lg">{selectedItem.nom_complet}</p>
                </div>
                {selectedItem.nom_arabe && (
                  <div>
                    <p className="text-sm text-gray-600">Nom arabe</p>
                    <p className="font-semibold">{selectedItem.nom_arabe}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Biographie</p>
                  <p className="font-semibold">{selectedItem.biographie}</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm text-gray-600">Titre</p>
                  <p className="font-semibold text-lg">{selectedItem.titre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Auteur</p>
                  <p className="font-semibold">{selectedItem.auteur_nom || selectedItem.auteur_autre || 'Inconnu'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Catégorie</p>
                    <p className="font-semibold">{selectedItem.categorie}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Langue</p>
                    <p className="font-semibold">{selectedItem.langue}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Modal Suppression */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsDeleteModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={confirmDelete} className="btn-danger">
              Supprimer
            </button>
          </div>
        }
      >
        <p className="text-lg">
          Êtes-vous sûr de vouloir supprimer{' '}
          <strong>
            {activeTab === 'marabouts' 
              ? `"${selectedItem?.nom_complet}"` 
              : `"${selectedItem?.titre}"`}
          </strong> ?
        </p>
      </Modal>
    </AdminLayout>
  );
};

